/* Migration v3.3 · WF-004 / WF-005 / WF-053 / WF-054 — the campaign page's Aperçu, as the reference draws it, on the
   app's own data: the chips row, « Ce que vos clients voient » (the recap of each visit, from the shape — a fixture
   campaign reads its one review), « Ce qui se passe ensuite » (three cards: the libraries, the supports that open it,
   the test as guidance), the welcome page in a phone, « Du parcours à l’installation » (four states, none a gate) and
   « Préparation des supports » (one row per support of its establishments, what it opens today, « Continuer »).
   Point 2 (wireframes ruling 20/10 § 2): on content not yet published (a brouillon, or a live campaign's next version) « Parcours
   à tester » is the blocker WF-004 / WF-005 draw — « Publier » waits until each visit of that content has its completed test, read
   from the draft's own test read (`pending`, lib-pack-22), never ticked here. The supports' rows keep the live version's state
   (point 4 kept: a staff QR shows once live; readiness() stays computed, point 3). */
import { Link } from 'react-router-dom'
import { Button, Icon, Note, Pill } from '@/ui'
import { draftTests, testDone, useCampaignTest, type CampaignStatus } from '@/lib/api'
import { campaignHasGift, flowOf, previewUrl, servedBy, shapeOf, textFor, layoutOf, getTypes, anyGiftOf, giftChoiceName, visitFlowOf, visitGiftOf, type Campaign, type CampaignLite, type Shape, type SupportRef, type Flow } from '@/lib/campaign'
import { getRoue, getWheelDesign, isCadeauUnique } from '@/lib/wheel'
import { currentWorld } from '@/mocks/worlds'
import WelcomeMini from './Welcome'
import { recapTail, visitRecaps } from './recap'

export interface ApercuProps {
  id: string; name: string; status: CampaignStatus; version: number
  local?: Campaign
  /** a fixture campaign's flow and roue name (mocks/data) */
  fixture?: { flow?: Flow; roue?: string }
  lite: CampaignLite[]; supports: SupportRef[]; names: Record<string, string>
  principale: boolean
  quota: boolean
  askName: (id: string) => string | undefined
  placeName: string
  onTab: (tab: string) => void
  onNav: (to: string) => void
}

export function Apercu(p: ApercuProps) {
  const { local, status, id } = p
  const draft = status === 'draft' || status === 'test'
  const noGift = local ? !campaignHasGift(local.recipe) : false
  const roue = local ? (local.recipe.roueId ? getRoue(local.recipe.roueId) : undefined) : undefined
  const giftType = local?.recipe.giftTypeId ? getTypes().find(t => t.id === local.recipe.giftTypeId) : undefined
  const giftName = giftType ? giftType.name : roue ? (isCadeauUnique(roue) ? roue.rewards[0]?.name ?? roue.name : roue.name) : local ? null : (p.fixture?.roue ?? 'Roue du salon')
  /* a fixture campaign (the demo's) has no shape: it invites to one Google review (Detail's own reading) */
  const shape: Shape = local ? shapeOf(local) : { type: 'simple', demande: { kind: 'avis', platforms: [{ platform: 'google', share: 100 }], askId: null, gift: { mode: 'roue' } }, steps: [] }
  const flow: Flow = local ? flowOf(local) : (p.fixture?.flow ?? 'cadeau_direct')
  /* pack 2b · each visit's own gift and order when the shape carries them (per-visit ruling P1), the campaign's otherwise */
  const recaps = visitRecaps(shape, { giftName, flow, countdown: local?.countdown ?? 30, earlyOpen: local?.earlyOpen ?? false, askName: p.askName,
    ...(local ? { perVisit: (i: number) => ({ giftName: giftChoiceName(visitGiftOf(local, i)), ...visitFlowOf(local, i) }) } : {}) })
  const tail = recapTail(shape, shape.after === 'stop' ? 'stop' : 'restart', local ? anyGiftOf(local) : !noGift)
  const multi = shape.type === 'parcours' && shape.steps.length > 1
  /* the supports of its establishments, what each opens today; the test read (MA-8), as guidance */
  const serving = p.supports.filter(s => servedBy(s, p.lite).current?.id === id)
  const { data: test } = useCampaignTest(id)
  const tested = !draft && testDone(test, noGift)
  /* point 2 · the content being written (a brouillon, or the next version of a live one): its tests, per visit, for this content */
  const writing = draft || !!local?.pending
  const counted = writing ? draftTests(test) : null
  const visitsN = shape.type === 'parcours' ? Math.max(1, shape.steps.length) : 1
  const writingTested = !!counted && counted.total > 0 && counted.done === counted.total
  const stateOf = (s: SupportRef): { label: string; tone: 'ok' | 'warn' | 'done' } => {
    const r = servedBy(s, p.lite)
    if (r.current?.id === id) return tested ? { label: 'Parcours testé', tone: 'ok' } : { label: 'Parcours à tester', tone: 'warn' }
    if (r.reason === 'principal_paused' && r.principal?.id === id) return { label: 'Destination à vérifier', tone: 'warn' }
    if (s.pinned === id) return { label: 'Destination à vérifier', tone: 'warn' }
    return { label: r.current ? `ouvre « ${r.current.name} »` : 'n’ouvre rien', tone: 'done' }
  }
  const preview = local ? () => window.open(previewUrl(local, { id: 'e', label: '', etape: 'entry' }, { monde: currentWorld() }), '_blank', 'noopener') : undefined
  const design = getWheelDesign()
  const statusLabel = p.quota ? 'En pause · quota atteint' : status === 'active' ? 'En ligne' : status === 'scheduled' ? 'Programmée' : status === 'paused' ? 'En pause' : status === 'ended' ? 'Terminée' : status === 'archived' ? 'Archivée' : 'Brouillon'
  const statusTone = status === 'active' ? 'ok' : status === 'ended' || status === 'archived' ? 'done' : 'warn'
  /* « Du parcours à l'installation »: four independent states — saved · tested · published · a support opens it */
  const strip: { label: string; done: boolean }[] = [
    { label: 'Parcours enregistré', done: true },
    { label: 'Version testée', done: draft ? writingTested : tested },
    { label: 'Campagne publiée', done: !draft && p.version > 0 },
    { label: 'Support installé', done: serving.length > 0 },
  ]
  return (
    <div className="apc" data-testid="apercu-v33">
      <div className="apc-main">
        <section className="card apc-card" data-testid="clients-voient">
          <div className="chips" style={{ marginBottom: 10 }}>
            <Pill tone={statusTone}>{statusLabel}</Pill>
            {p.principale && <Pill tone="ok" mark="">Campagne principale</Pill>}
            {!draft && <Pill tone="ink">Version {p.version}</Pill>}
          </div>
          <h2 className="t-section" style={{ fontSize: 20 }}>{draft ? 'Votre parcours en préparation' : 'Ce que vos clients voient'}</h2>
          {draft && <p className="f-help" style={{ margin: '4px 0 0' }}>Brouillon · aucun client ne voit encore ce parcours.</p>}
          <ul className="apc-list">
            <li>{giftName ?? 'Sans cadeau — la demande, puis un merci'}</li>
            {recaps.map(r => <li key={r.n}><b>Visite {r.n}&nbsp;:</b> {r.demande}{r.moment ? <><br /><span className="f-help">{r.moment}{r.extra ? ` · ${r.extra}` : ''}</span></> : null}</li>)}
            {multi && <li>{tail[1]}</li>}
            {!noGift && <li>Le cadeau ne dépend jamais de la réponse.</li>}
          </ul>
          {!draft && <p className="f-help" style={{ margin: '8px 0 0' }}>Les parcours commencés conservent leur version. Un brouillon ne change pas le parcours publié.</p>}
        </section>

        <section className="card apc-card" data-testid="ensuite">
          <h2 className="t-section" style={{ fontSize: 20 }}>Ce qui se passe ensuite</h2>
          <div className="apc-row">
            <div><b>Contenus de ce parcours</b><br /><span className="f-help">Les bibliothèques préparent les prochaines publications.</span></div>
            <Button size="sm" onClick={() => p.onNav(roue ? `/cadeaux?onglet=roues&modifier=${roue.id}` : '/cadeaux')}>Voir les contenus</Button>
          </div>
          <div className="apc-row">
            <div><b>{serving.length} support{serving.length > 1 ? 's' : ''} relié{serving.length > 1 ? 's' : ''}</b><br /><span className="f-help">Le code permanent reste identique si vous changez sa destination.</span></div>
            <Button size="sm" onClick={() => p.onTab('supports')}>Installer</Button>
          </div>
        </section>
      </div>

      <aside className="apc-side">
        <section className="card apc-card" data-testid="pret-clients">
          <p className="t-eyebrow">Prêt pour vos clients&nbsp;?</p>
          <p className="t-body" style={{ margin: '6px 0 4px', fontWeight: 700, color: 'var(--bmr-text)' }}>Vérifiez le parcours complet.</p>
          <p className="t-body" style={{ margin: '0 0 10px' }}>Testez chaque visite et essayez Passer. {noGift ? '' : 'Validez en caisse test les visites qui attribuent un cadeau.'}</p>
          {writing && (
            <div data-testid="apercu-bloquant" style={{ display: 'grid', gap: 4, margin: '0 0 10px' }}>
              <span><Pill tone={writingTested ? 'ok' : 'warn'}>{writingTested ? 'Parcours testé' : 'Parcours à tester'}</Pill></span>
              <span className="f-help" style={{ margin: 0 }} data-testid="apercu-visites-testees">{counted?.done ?? 0} / {counted?.total || visitsN} visite{(counted?.total || visitsN) > 1 ? 's' : ''} testée{(counted?.total || visitsN) > 1 ? 's' : ''} pour ce contenu.</span>
              {!writingTested && <span className="f-help" style={{ margin: 0 }}>La publication attend un test terminé pour chaque visite de ce contenu.</span>}
            </div>
          )}
          {writing
            ? <Button size="sm" variant="primary" onClick={() => p.onNav(`/campagnes/nouvelle?brouillon=${id}&etape=3`)} data-testid="apercu-tester"><Icon name="phone" /> Tester le parcours</Button>
            : preview
            ? <Button size="sm" variant="primary" onClick={preview}><Icon name="phone" /> Tester le parcours</Button>
            : <Button size="sm" variant="primary" onClick={() => p.onTab('supports')}><Icon name="qr" /> Tester le parcours</Button>}
          <p className="f-help" style={{ margin: '8px 0 0' }}>Aucun e-mail, aucune activité réelle.{tested ? ' Dernier test : cadeau validé en caisse test.' : ''}</p>
        </section>
        {local && (
          <div className="apc-phone" data-testid="apercu-accueil">
            <div className="bld-phone">
              <WelcomeMini width={280} layout={layoutOf(local.look, design.a)} colour={design.a} logo={design.logo} logoUrl={design.logoUrl ?? null} photo={local.look?.photo ?? null}
                sentence={textFor(local, 'entry.h', noGift).replace('{établissement}', p.placeName)} promise={textFor(local, 'entry.p', noGift).replace('{établissement}', p.placeName)} brand={p.placeName.split(' ')[0] || 'Valessio'} place={p.placeName} design={roue?.design ?? design} noGift={noGift} />
            </div>
            <p className="f-help" style={{ margin: '8px 0 0', textAlign: 'center' }}>Aperçu de l’accueil</p>
          </div>
        )}
      </aside>

      <section className="card apc-card apc-wide" data-testid="parcours-installation">
        <h2 className="t-section" style={{ fontSize: 20 }}>Du parcours à l’installation</h2>
        <ol className="apc-strip">
          {strip.map(s => <li key={s.label} className={s.done ? 'done' : ''}><span className="n" aria-hidden>{s.done ? '✓' : '→'}</span>{s.label}</li>)}
        </ol>
        <p className="f-help" style={{ margin: '10px 0 0' }}>Un support installé peut être indisponible si sa campagne est en pause. Chaque étape reste indépendante.</p>
        {status === 'paused' && !p.quota && <div style={{ marginTop: 10 }}><Note tone="candour"><b>En pause.</b> Aucun nouveau cadeau n’est attribué&nbsp;; les cadeaux déjà gagnés restent valables. «&nbsp;Reprendre la diffusion&nbsp;»&nbsp;: onglet <a href="#" onClick={e => { e.preventDefault(); p.onTab('parametres') }}>Paramètres</a>.</Note></div>}
      </section>

      {status === 'archived' && (
        <section className="card apc-card apc-wide" data-testid="organiser">
          <h2 className="t-section" style={{ fontSize: 20 }}>Organiser cette campagne</h2>
          <p className="t-body" style={{ margin: 0 }}>Archivée. Le contenu et l’historique restent conservés. La restauration ne publie rien.</p>
        </section>
      )}

      <section className="card apc-card apc-wide" data-testid="preparation-supports">
        <h2 className="t-section" style={{ fontSize: 20 }}>Préparation des supports</h2>
        {p.supports.length === 0
          ? <p className="f-help" style={{ margin: '6px 0 0' }}>Aucun support pour l’instant — <Link to="/supports?vue=modeles">Créer un support</Link>.</p>
          : (<div className="tbl-wrap" style={{ marginTop: 10 }}><table className="tbl">
              <thead><tr><th>Support</th><th>État</th><th><span className="sr-only">Continuer</span></th></tr></thead>
              <tbody>{p.supports.map(s => { const st = stateOf(s); return (
                <tr key={s.id}>
                  <td>{s.name}{s.kind === 'personnel' ? ' · support personnel' : ''}{s.locationId && Object.keys(p.names).length > 1 ? <span className="f-help"> · {p.names[s.locationId] ?? s.locationId}</span> : null}</td>
                  <td><Pill tone={st.tone}>{st.label}</Pill></td>
                  <td style={{ textAlign: 'right' }}><Button size="sm" variant="quiet" onClick={() => p.onNav(`/supports?vue=detail&id=${s.id}`)}>Continuer</Button></td>
                </tr>) })}</tbody>
            </table></div>)}
        <div style={{ marginTop: 10 }}><Button size="sm" onClick={() => p.onNav(draft ? '/supports?vue=modeles' : `/campagnes/nouvelle?brouillon=${id}&etape=4`)}><Icon name="plus" /> Créer un support</Button></div>
      </section>
    </div>
  )
}
